
import React from "react";
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from "@material-ui/core";
import CloseIconModal from "assets/icons/CloseIconModal";
import "./index.scss";

import { NewAutoCompleteField } from "components/NewAutoCompleteField";
import { Row } from "components/Row";
import { Col } from "components/Col";
import { getElementID } from "utils/HTMLUtils";

import { useSelector } from "react-redux";
import { RootState } from "store";

import { CustomTextField } from "components/CustomTextField";
import { AREA_ESTUDO_MAX_LENGTH } from "utils/ValidationUtils";

type SelectOption = {
  label: string;
  value: number;
};

type AssessorEditing = {
  formacaoAcademica: number;
  situacao: number;
  areaEstudo?: string;
};

interface FormacaoAcademicaProps {
  open: boolean;
  onClose: () => void;
  assessorEditing: AssessorEditing;
  onChangeField: (
    name: "formacaoAcademica" | "situacao" | "areaEstudo",
    value: number | string
  ) => void;
  onAdd: (
    payload: Array<{
      id: null;
      diploma: number;
      areaEstudo: string;
      situacao: number;
    }>
  ) => void;
  disableBackdropClick?: boolean;
  disableEscapeKeyDown?: boolean;
}

const rootElementId = "assessor-edit-modal";

const FormacaoAcademica: React.FC<FormacaoAcademicaProps> = ({
  open,
  onClose,
  assessorEditing,
  onChangeField,
  onAdd,
  disableBackdropClick = false,
  disableEscapeKeyDown = false,
}) => {
  const languageValues = useSelector(
    (state: RootState) => state.languages.languageValues
  );

  const allFormacaoAcademicaOptions = React.useMemo<SelectOption[]>(
    () => [
      { label: "Graduação", value: 0 },
      { label: "Pós-graduação", value: 1 },
      { label: "Mestrado", value: 2 },
      { label: "Doutorado", value: 3 },
    ],
    []
  );

  const allSituacoesOptions = React.useMemo<SelectOption[]>(
    () => [
      { label: "Em andamento", value: 0 },
      { label: "Concluída", value: 1 },
    ],
    []
  );

  const [formacaoTouched, setFormacaoTouched] = React.useState(false);
  const [situacaoTouched, setSituacaoTouched] = React.useState(false);
  const [areaEstudoTouched, setAreaEstudoTouched] = React.useState(false);

  const formacaoVal = React.useMemo(() => {
    const ok = (assessorEditing.formacaoAcademica ?? -1) >= 0;
    return {
      status: ok,
      error:
        ok
          ? undefined
          : languageValues?.validations?.idFormacaoAcademicas?.required ??
          "Campo obrigatório",
    };
  }, [assessorEditing.formacaoAcademica, languageValues]);

  const situacaoVal = React.useMemo(() => {
    const ok = (assessorEditing.situacao ?? -1) >= 0;
    return {
      status: ok,
      error:
        ok
          ? undefined
          : languageValues?.validations?.situacao?.required ??
          "Campo obrigatório",
    };
  }, [assessorEditing.situacao, languageValues]);

  const areaEstudoText = (assessorEditing.areaEstudo ?? "").trim();

  const areaEstudoVal = React.useMemo(() => {
    const ok = areaEstudoText.length > 0;
    return {
      status: ok,
      error:
        ok
          ? undefined
          : "Campo obrigatório",
    };
  }, [areaEstudoText]);

  const isFormValid = formacaoVal.status && situacaoVal.status && areaEstudoVal.status;

  const handleChangeFormacao = React.useCallback(
    (value: SelectOption | null) => {
      setFormacaoTouched(true);
      const currValue = value ? value.value : -1;
      onChangeField("formacaoAcademica", currValue);
    },
    [onChangeField]
  );

  const handleChangeSituacao = React.useCallback(
    (value: SelectOption | null) => {
      setSituacaoTouched(true);
      const currValue = value ? value.value : -1;
      onChangeField("situacao", currValue);
    },
    [onChangeField]
  );

  const handleChangeAreaEstudo = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setAreaEstudoTouched(true);
      onChangeField("areaEstudo", event.target.value ?? "");
    },
    [onChangeField]
  );

  const handleAdd = React.useCallback(() => {
    if (!isFormValid) {
      setFormacaoTouched(true);
      setSituacaoTouched(true);
      setAreaEstudoTouched(true);
      return;
    }

    const payload = [
      {
        id: null,
        diploma: assessorEditing.formacaoAcademica,
        areaEstudo: areaEstudoText,
        situacao: assessorEditing.situacao,
      },
    ];
    onAdd(payload);
    onClose();
  }, [isFormValid, assessorEditing.formacaoAcademica, assessorEditing.situacao, areaEstudoText, onAdd, onClose]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      closeAfterTransition
      disableBackdropClick={disableBackdropClick}
      disableEscapeKeyDown={disableEscapeKeyDown}
      PaperProps={{ className: "FormacaoAdemica_paper" }}
      style={{ zIndex: 9999 }}
    >
      <Box>
        <DialogTitle className="FormacaoAdemica_dialogTitle">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography className="FormacaoAdemica_titleModal">
              {"Formações acadêmicas"}
            </Typography>

            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              size="small"
            >
              <CloseIconModal style={{ width: "auto", height: 25 }} />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={1}>
            <Grid item md={12}>
              <Row>
                <Col cols={[12, 12, 12, 12, 12]}>
                  <NewAutoCompleteField
                    id={getElementID(rootElementId, "formacaoAcademica")}
                    label={
                      languageValues?.cadastroAssessorFormacaoAcademica ?? "Formação acadêmica"
                    }
                    placeholder={"Selecione uma opção"}
                    options={allFormacaoAcademicaOptions}
                    value={assessorEditing.formacaoAcademica}
                    onChange={handleChangeFormacao}
                    error={formacaoTouched && !formacaoVal.status}
                    helperText={formacaoTouched && !formacaoVal.status ? formacaoVal.error : ""}
                  />
                </Col>

                <Col cols={[12, 12, 12, 12, 12]}>
                  <NewAutoCompleteField
                    id={getElementID(rootElementId, "situacao")}
                    label={languageValues?.cadastroAssessorSituacao ?? "Situação"}
                    placeholder={"Selecione uma opção"}
                    options={allSituacoesOptions}
                    value={assessorEditing.situacao}
                    onChange={handleChangeSituacao}
                    error={situacaoTouched && !situacaoVal.status}
                    helperText={situacaoTouched && !situacaoVal.status ? situacaoVal.error : ""}
                  />
                </Col>

                <Col cols={[12, 12, 12, 12, 12]}>

                  <CustomTextField
                    id={getElementID(rootElementId, "areaEstudo")}
                    placeholder={
                      languageValues?.cadastroAssessorQualificacaoPlaceholder ??
                      "Informe a qualificação/área de estudo"
                    }
                    label={languageValues?.cadastroAssessorAreaEstudo ?? "Área de estudo"}
                    name="areaEstudo"
                    value={assessorEditing.areaEstudo ?? ""}
                    onChange={handleChangeAreaEstudo}
                    onBlur={() => setAreaEstudoTouched(true)}
                    counter
                    maxSize={AREA_ESTUDO_MAX_LENGTH}
                    error={areaEstudoTouched && !areaEstudoVal.status}                
                    helperText={areaEstudoTouched && !areaEstudoVal.status ? areaEstudoVal.error : ""}  
                  />

                </Col>
              </Row>
            </Grid>
          </Grid>
        </DialogContent>

        <Container className="FormacaoAdemica_dialogActions" disableGutters>
          <Box className="FormacaoAdemica_container-box-btn">
            <div className="FormacaoAdemica_btn">
              <Button
                className="FormacaoAdemica_action-button"
                variant="contained"
                color="inherit"
                onClick={handleAdd}
                disabled={!isFormValid}
              >
                {"Adicionar"}
              </Button>
            </div>
          </Box>
        </Container>
      </Box>
    </Dialog>
  );
};

export default FormacaoAcademica;
